<template>
  <div class="flex-1 flex flex-col overflow-hidden bg-white p-3">

    <div class="grid grid-cols-6 gap-2 py-2">
      <input
        v-model="search"
        type="text"
        placeholder="Search"
        class="col-span-2 w-full px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        @keyup.enter="fetch" />
      <select v-model="type" @change="fetch"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary">
        <option value="">-- Select Type --</option>
        <option value="AGREEMENT">Agreements</option>
        <option value="GUARANTEE">Guarantee</option>
        <option value="POWER_OF_ATTORNEY">Powers of Attorney</option>
        <option value="AFFIDAVIT">Affidavit</option>
        <option value="STATUTORY_DECLARATION">Statutory Declaration</option>
        <option value="CHATTEL">Chattel</option>
        <option value="DEBENTURE">Debenture</option>
        <option value="DEMAND_PROMISSORY_NOTE">Demand Promissory Note</option>
        <option value="REPORT_OF_DIRECTORS">Report of Directors</option>
        <option value="CONSTITUTION">Constitution</option>
        <option value="FACILITY_LETTER">Loan Facility Letter</option>
        <option value="DEED_OF_ASSIGNMENT">Deed of Assignment</option>
        <option value="DEED_POLL">DeedPoll</option>
        <option value="MORTGAGE_DEED">Mortgage Deed</option>
        <option value="SPOUSAL_CONSENT">Spousal Consent</option>
        <option value="WILL">Will</option>
        <option value="INDEPENDENT_ADVICE_FORM">Independent Advice Form</option>
        <option value="resolution">Resolution as document</option>
        <option value="OTHER">Others</option>
      </select>
      <div class="col-span-3 flex justify-end">
        <button type="button" class="btn-sm-primary" @click="showImportModal = true">
          <i class="fa fa-upload mr-1"></i> Import documents
        </button>
      </div>
    </div>

    <div class="bg-white shadow overflow-hidden flex flex-col">
      <div class="overflow-x-auto flex-1">
        <table class="w-full text-xs">
          <thead class="bg-gray-50 border-b border-gray-100 sticky top-0">
          <tr>
            <th class="text-left py-3 px-4 font-semibold text-gray-700">Instrument No.</th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700">Document Type</th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700">Subject</th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700">Filing Date</th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700">Execution Date</th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
          <template v-if="!loading && documents.length > 0">
            <tr v-for="(document, idx) in documents" :key="idx"
                class="border-b border-gray-100 hover:bg-gray-50 transition">
              <td class="py-3 px-4 font-bold text-primary cursor-pointer hover:underline"
                  @click="viewFile(document.instrumentNumber)">
                  {{ document.instrumentNumber }}
              </td>
              <td class="py-3 px-4 capitalize">{{ cleanType(document.documentType.toLowerCase()) }}</td>
              <td class="py-3 px-4 capitalize">{{ cleanType(document.subjectMatter) }}</td>
              <td class="py-3 px-4">{{ dateFormat(document.filingDate) }}</td>
              <td class="py-3 px-4">{{ dateFormat(document.dateOfExecution) }}</td>
              <td class="py-3 px-4">
                  <span
                    class="text-xs font-medium px-3 py-1 rounded-full inline-block text-green-700 font-bold bg-green-100">
                    Registered
                  </span>
              </td>
              <td class="py-3 px-4">
                <button @click="viewFile(document.instrumentNumber)" class="btn-sm-primary" title="View">
                  <i class="fa fa-eye"></i> View
                </button>
              </td>
            </tr>
          </template>
          <tr v-if="loading">
            <td colspan="7" class="py-16 text-center">
              <i class="fa fa-spinner fa-spin text-primary text-2xl"></i>
            </td>
          </tr>
          <template v-if="!loading && documents.length === 0">
            <tr>
              <td colspan="7" class="py-16 text-center">
                <div class="flex flex-col items-center justify-center text-gray-400">
                  <i class="fa fa-folder-open text-6xl mb-4"></i>
                  <p class="text-lg font-medium text-gray-500">No Legal Documents Found</p>
                  <p class="text-sm text-gray-400 mt-2">No registered legal documents in the registry.</p>
                </div>
              </td>
            </tr>
          </template>
          </tbody>
        </table>
      </div>
      <Pagination :pagination="pagination" :page="currentPage" :limit="limit" label="documents" @update="update" />
    </div>

    <AppModal v-model="showImportModal" size="lg" title="Import legal documents" hide-footer>
      <div class="p-1">
        <p class="text-sm text-gray-500 mb-4">
          Select the document type and upload a CSV file to import legal documents.
        </p>
        <LegalDocumentsImport @success="onImportSuccess" />
      </div>
    </AppModal>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import AppModal from "@/components/ui/AppModal.vue";
import { useToast } from "@/composables";
import { useLegalDocumentsRegistryStore } from "@/stores/legal-documents-registry";
import { dateFormat } from "@/commons/composables/utilities";
import Pagination from "@/commons/components/Pagination.vue";
import LegalDocumentsImport from "@/domains/home/services/registry/legal-documents/LegalDocumentsImport.vue";
import type { Pagination as PaginationType } from "@/app/types";

interface Document {
  instrumentNumber: string
  documentType: string
  subjectMatter: string
  filingDate: string
  dateOfExecution: string
}

const store = useLegalDocumentsRegistryStore();
const router = useRouter()
const { errorToast } = useToast();

const loading = ref(false)
const currentPage = ref(1)
const limit = ref(15)
const documents = ref<Document[]>([])
const pagination = ref<PaginationType | undefined>(undefined);
const search = ref("")
const type = ref("")
const showImportModal = ref(false)

onMounted(() => {
  fetch()
})

const fetch = () => {
  loading.value = true
  store.fetchLegalDocumentsRegistry({
    search: search.value,
    page: currentPage.value,
    limit: limit.value,
    type: type.value
  });
}

const viewFile = (id: string) => {
  router.push("/home/non-individuals/document-viewer/" + id)
  router.go(0)
}

const cleanType = (name: string) => {
  const n = name.replaceAll("_", " ");
  return n.charAt(0).toUpperCase() + n.slice(1);
};

function update(pg: number) {
  currentPage.value = pg;
  fetch();
}

const onImportSuccess = () => {
  showImportModal.value = false;
  fetch();
};

watch(search, () => {
  fetch()
})

watch(type, () => {
  fetch()
})

watch(() => store.document_registry, (data) => {
  if (data) {
    loading.value = false
    documents.value = data.content || [];
    pagination.value = {
      currentPage: currentPage.value,
      nextPage: currentPage.value + 1,
      totalRows: data.totalElements || 0,
      totalPages: data.totalPages || 0
    };
  }
})

watch(() => store.error, (data) => {
  if (data) {
    loading.value = false
    errorToast("Error", JSON.stringify(data.message))
  }
})
</script>
